/**
 * KATA Board API Service
 * Handles all KATA Board related API calls
 */

import apiClient from './api';

// Types
export interface KATACycle {
  id: number;
  title: string;
  description: string;
  workshop: number;
  workshop_title: string;
  current_condition: string;
  target_condition: string;
  obstacle: string;
  success_criteria: string;
  status: 'planning' | 'doing' | 'checking' | 'acting' | 'completed' | 'paused';
  cycle_number: number;
  coach?: User;
  learner?: User;
  coach_id?: number;
  learner_id?: number;
  start_date: string;
  target_completion_date?: string;
  actual_completion_date?: string;
  actual_results: string;
  pdca_steps: PDCAStep[];
  notes: KATANote[];
  current_step?: PDCAStep;
  progress_percentage: number;
  created_at: string;
  updated_at: string;
}

export interface PDCAStep {
  id: number;
  kata_cycle: number;
  step_type: 'plan' | 'do' | 'check' | 'act';
  title: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'blocked';
  order: number;
  is_current: boolean;
  assigned_to?: User;
  assigned_to_id?: number;
  planned_start_date?: string;
  planned_end_date?: string;
  actual_start_date?: string;
  actual_end_date?: string;
  results: string;
  next_actions: string;
  experiments: Experiment[];
  notes: KATANote[];
  created_at: string;
  updated_at: string;
}

export interface Experiment {
  id: number;
  pdca_step: number;
  title: string;
  hypothesis: string;
  method: string;
  status: 'planned' | 'running' | 'completed' | 'cancelled';
  start_date?: string;
  end_date?: string;
  actual_results: string;
  learnings: string;
  success?: boolean;
  baseline_metric?: number;
  target_metric?: number;
  actual_metric?: number;
  metric_unit: string;
  duration_days?: number;
  metric_improvement?: number;
  created_at: string;
  updated_at: string;
}

export interface KATANote {
  id: number;
  kata_cycle: number;
  pdca_step?: number;
  note_type: 'observation' | 'learning' | 'obstacle' | 'idea' | 'decision';
  title: string;
  content: string;
  author: User;
  is_important: boolean;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
}

export interface WorkshopKATA {
  id: number;
  title: string;
  improvement_description: string;
  status: string;
  plant: number;
  department: number;
  kata_cycles: KATACycle[];
  active_kata_cycles: KATACycle[];
}

export interface KATABoardData {
  workshop: WorkshopKATA;
  kata_cycles: KATACycle[];
  active_cycle?: KATACycle;
  board_stats: {
    total_cycles: number;
    active_cycles: number;
    total_experiments: number;
    successful_experiments: number;
  };
}

export interface KATADashboardStats {
  total_cycles: number;
  active_cycles: number;
  completed_cycles: number;
  cycles_by_status: Record<string, number>;
  avg_cycle_duration?: number;
  recent_activity: KATACycle[];
}

// KATA Cycle API
export const kataCycleApi = {
  // List KATA cycles
  list: (params?: {
    workshop?: number;
    status?: string;
    coach?: number;
    learner?: number;
  }) => apiClient.get<KATACycle[]>('/kata-cycles/', { params }),

  // Get KATA cycle details
  get: (id: number) => apiClient.get<KATACycle>(`/kata-cycles/${id}/`),

  // Create new KATA cycle
  create: (data: {
    title: string;
    description: string;
    workshop: number;
    current_condition: string;
    target_condition: string;
    obstacle?: string;
    success_criteria: string;
    cycle_number?: number;
    coach_id?: number;
    learner_id?: number;
    target_completion_date?: string;
  }) => apiClient.post<KATACycle>('/kata-cycles/', data),

  // Update KATA cycle
  update: (id: number, data: Partial<KATACycle>) => 
    apiClient.patch<KATACycle>(`/kata-cycles/${id}/`, data),

  // Delete KATA cycle
  delete: (id: number) => apiClient.delete(`/kata-cycles/${id}/`),

  // Advance to next PDCA step
  advanceStep: (id: number) => 
    apiClient.post(`/kata-cycles/${id}/advance_step/`),

  // Start new cycle based on current one
  startNewCycle: (id: number, data: {
    description?: string;
    current_condition?: string;
    target_condition: string;
    obstacle?: string;
    success_criteria: string;
  }) => apiClient.post<KATACycle>(`/kata-cycles/${id}/start_new_cycle/`, data),

  // Get dashboard statistics
  getDashboardStats: () => 
    apiClient.get<KATADashboardStats>('/kata-cycles/dashboard_stats/'),
};

// PDCA Step API
export const pdcaStepApi = {
  // List PDCA steps
  list: (params?: { kata_cycle?: number }) => 
    apiClient.get<PDCAStep[]>('/pdca-steps/', { params }),

  // Get PDCA step details
  get: (id: number) => apiClient.get<PDCAStep>(`/pdca-steps/${id}/`),

  // Update PDCA step
  update: (id: number, data: {
    title?: string;
    description?: string;
    status?: string;
    is_current?: boolean;
    assigned_to_id?: number;
    planned_start_date?: string;
    planned_end_date?: string;
    actual_start_date?: string;
    actual_end_date?: string;
    results?: string;
    next_actions?: string;
  }) => apiClient.patch<PDCAStep>(`/pdca-steps/${id}/`, data),

  // Mark step as current
  markCurrent: (id: number) => 
    apiClient.post(`/pdca-steps/${id}/mark_current/`),
};

// Experiment API
export const experimentApi = {
  // List experiments
  list: (params?: { pdca_step?: number; kata_cycle?: number }) => 
    apiClient.get<Experiment[]>('/experiments/', { params }),

  // Get experiment details
  get: (id: number) => apiClient.get<Experiment>(`/experiments/${id}/`),

  // Create new experiment
  create: (data: {
    pdca_step: number;
    title: string;
    hypothesis: string;
    method: string;
    baseline_metric?: number;
    target_metric?: number;
    metric_unit?: string;
    start_date?: string;
    end_date?: string;
  }) => apiClient.post<Experiment>('/experiments/', data),

  // Update experiment
  update: (id: number, data: Partial<Experiment>) => 
    apiClient.patch<Experiment>(`/experiments/${id}/`, data),

  // Complete experiment with results
  complete: (id: number, data: {
    actual_results: string;
    learnings: string;
    success?: boolean;
    actual_metric?: number;
  }) => apiClient.post(`/experiments/${id}/complete_experiment/`, data),

  // Delete experiment
  delete: (id: number) => apiClient.delete(`/experiments/${id}/`),
};

// KATA Note API
export const kataNoteApi = {
  // List notes
  list: (params?: { 
    kata_cycle?: number; 
    pdca_step?: number; 
    note_type?: string; 
  }) => apiClient.get<KATANote[]>('/kata-notes/', { params }),

  // Get note details
  get: (id: number) => apiClient.get<KATANote>(`/kata-notes/${id}/`),

  // Create new note
  create: (data: {
    kata_cycle: number;
    pdca_step?: number;
    note_type: string;
    title?: string;
    content: string;
    is_important?: boolean;
  }) => apiClient.post<KATANote>('/kata-notes/', data),

  // Update note
  update: (id: number, data: Partial<KATANote>) => 
    apiClient.patch<KATANote>(`/kata-notes/${id}/`, data),

  // Delete note
  delete: (id: number) => apiClient.delete(`/kata-notes/${id}/`),
};

// Workshop KATA API
export const workshopKataApi = {
  // List workshops with KATA context
  list: () => apiClient.get<WorkshopKATA[]>('/workshops-kata/'),

  // Get workshop KATA details
  get: (id: number) => apiClient.get<WorkshopKATA>(`/workshops-kata/${id}/`),

  // Get complete KATA board for workshop
  getKataBoard: (id: number) => 
    apiClient.get<KATABoardData>(`/workshops-kata/${id}/kata_board/`),
};

// Utility functions
export const kataUtils = {
  // Get step type display name
  getStepTypeDisplay: (stepType: string) => {
    const displays = {
      plan: 'Plan',
      do: 'Do',
      check: 'Check',
      act: 'Act'
    };
    return displays[stepType as keyof typeof displays] || stepType;
  },

  // Get status display name
  getStatusDisplay: (status: string) => {
    const displays = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed',
      blocked: 'Blocked',
      planning: 'Planning',
      doing: 'Doing',
      checking: 'Checking',
      acting: 'Acting',
      paused: 'Paused'
    };
    return displays[status as keyof typeof displays] || status;
  },

  // Get status color
  getStatusColor: (status: string) => {
    const colors = {
      not_started: 'gray',
      in_progress: 'blue',
      completed: 'green',
      blocked: 'red',
      planning: 'yellow',
      doing: 'blue',
      checking: 'orange',
      acting: 'purple',
      paused: 'gray'
    };
    return colors[status as keyof typeof colors] || 'gray';
  },

  // Get step type color
  getStepTypeColor: (stepType: string) => {
    const colors = {
      plan: 'blue',
      do: 'green',
      check: 'orange',
      act: 'purple'
    };
    return colors[stepType as keyof typeof colors] || 'gray';
  },

  // Calculate progress percentage
  calculateProgress: (steps: PDCAStep[]) => {
    if (steps.length === 0) return 0;
    const completedSteps = steps.filter(step => step.status === 'completed').length;
    return Math.round((completedSteps / steps.length) * 100);
  },

  // Format duration
  formatDuration: (days?: number) => {
    if (!days) return 'N/A';
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.round(days / 7)} weeks`;
    return `${Math.round(days / 30)} months`;
  },

  // Format metric improvement
  formatMetricImprovement: (improvement?: number) => {
    if (improvement === undefined || improvement === null) return 'N/A';
    const sign = improvement >= 0 ? '+' : '';
    return `${sign}${improvement.toFixed(1)}%`;
  }
};
